/**
 * DSP 派送任务相关类型定义
 * 
 * 本文件包含 DSP 派送任务模块的所有类型定义，
 * 包括任务状态、包裹信息、司机数据、地图视图等相关接口和枚举。
 */

// ==================== 枚举定义 ====================

/**
 * 任务状态枚举
 * 定义派送任务在整个生命周期中的各个状态
 */
export enum TaskStatus {
    CREATED = 'CREATED', // 待司机领取
    DRIVER_PICKINGUP = 'DRIVER_PICKINGUP', // 司机领取中
    DELIVERING = 'DELIVERING', // 派送中
    COMPLETED = 'COMPLETED' // 已完成
}

/**
 * 包裹状态枚举
 * 与后端的 OverallStatus 枚举对应，定义单个包裹的派送状态
 */
export enum ParcelStatus {
    DELIVERY_IN_PROGRESS = 'DELIVERY_IN_PROGRESS', // 派送中
    PRE_DELIVERED = 'PRE_DELIVERED', // 预妥投
    DELIVERED = 'DELIVERED', // 已妥投
    DELIVERY_FAILED = 'DELIVERY_FAILED' // 派送失败
}

/**
 * 地图视图模式枚举
 * 定义地图页面中数据的分组显示方式
 */
export enum MapViewMode {
    BY_ROUTE = 'by_route', // 按线路分组显示
    BY_DRIVER = 'by_driver' // 按司机分组显示
}

// ==================== 核心业务接口 ====================

/**
 * 派送任务数据接口
 * 包含任务的基本信息、统计数据、关联的司机和包裹信息
 */
export interface DeliveryTask {
    id: string;
    routeHistoryId: number;
    routeName: string;
    driverAccountId: number;
    areaId?: number;
    warehouseId?: number;
    dspId?: number;
    taskDate?: string; // YYYY-MM-DD 格式
    status: string; // TaskStatus 枚举值
    assignedParcels?: number;
    actualPickedUpParcels?: number;
    transferInParcels?: number;
    transferOutParcels?: number;
    createdAt?: string; // ISO 8601 格式
    updatedAt?: string; // ISO 8601 格式
    sortingCode?: string;
    
    // 司机姓名相关字段
    actualDriverName?: string; // 当前负责的司机姓名（列表和详情都有）
    originalDriverName?: string; // 初始指派的司机姓名（仅详情页有）
    
    // 详情页专用字段
    parcels?: Parcel[];

    // 新增分组/子任务相关字段：
    isGroupRow?: boolean;
    totalTasks?: number;
    totalAssignedParcels?: number;
    totalActualPickedUp?: number;
    children?: DeliveryTask[];
}

/**
 * 任务详情接口
 * 用于任务详情页面显示，包含统计数据和包裹列表
 */
export interface TaskDetail {
    id: string;
    sortingCode: string;
    driverName: string;
    allocatedCount: number; // 分配单量
    receivedCount: number; // 司机领取
    deliveredCount: number; // 已妥投
    deliveryRate: number; // 妥投率 (0-100)
    deliveringCount: number; // 派送中
    failedCount: number; // 派送失败
    allCount: number; // 全部数量（过滤掉 TRANSFERRED_OUT）
    transferInCount: number; // 转入包裹
    transferOutCount: number; // 转出包裹
    parcels: Parcel[];
}

/**
 * 包裹信息接口
 * 包含包裹的基本信息、收件人信息、派送状态和位置信息
 */
export interface Parcel {
    id: string;
    trackingNumber: string;
    stopNumber: string;
    subStopNumber: string;
    address: string;
    status: string; // ParcelStatus 枚举值
    pickupStatus?: string; // 领取状态，用于过滤 TRANSFERRED_OUT
    recipientName?: string;
    recipientPhoneNumber?: string;
    recipientPhoneExtension?: string;
    deliverAt?: string; // ISO 8601 格式
    deliveryAttempts?: number;
    preDeliveredAt?: string; // ISO 8601 格式
    deliveredAt?: string; // ISO 8601 格式
    exceptionReasonCode?: string;
    exceptionReason?: ExceptionDictionaryType;
    operatedAt?: string; // ISO 8601 格式
    latitude?: number;
    longitude?: number;
    routeName?: string;
    clusterInfo?: {
        isCluster?: boolean;
        clusterCount?: number;
        clusterAddresses?: Parcel[];
    };
}

/**
 * 运单数据接口
 * 用于地图页面显示，包含运单的基本信息和位置信息
 */
export interface DeliveryOrder {
    id: string;
    trackingNumber: string;
    address: string;
    latitude: number;
    longitude: number;
    area: string;
    driverId: string;
    driverName: string;
    status: string;
    taskDate: string; // YYYY-MM-DD 格式
    recipientName?: string;
    recipientPhoneNumber?: string;
    recipientPhoneExtension?: string;
    deliverAt?: string; // ISO 8601 格式
    deliveryAttempts?: number;
    deliveredAt?: string; // ISO 8601 格式
    exceptionReasonCode?: string;
    routeName?: string;
    stopNumber: string;
    subStopNumber: string;
}

// ==================== 辅助接口 ====================

/**
 * 司机数据接口
 */
export interface Driver {
    id: string;
    name: string;
}

/**
 * 异常字典接口
 * 定义派送异常的类型和描述信息
 */
export interface ExceptionDictionaryType {
    exceptionReasonCode: string;
    primaryType: string;
    secondaryTypeZh: string;
    secondaryTypeEn: string;
    secondaryTypeEs?: string;
    actionDescription: string;
    isUploadsImages: boolean;
}

// ==================== 查询和过滤接口 ====================

/**
 * 派送任务过滤条件接口
 * 用于任务列表页面的筛选功能
 */
export interface DeliveryTaskFilter {
    taskDateStart?: string; // YYYY-MM-DD 格式
    taskDateEnd?: string; // YYYY-MM-DD 格式
    publishDate?: {
        start: string; // YYYY-MM-DD 格式
        end: string; // YYYY-MM-DD 格式
    };
    areaId?: number;
    driverIds?: number[];
    status?: string[];
}

// ==================== GraphQL 查询结果接口 ====================

/**
 * 运单分组接口
 * 用于地图页面按线路或司机分组显示运单
 */
export interface DeliveryOrderGroup {
    groupKey: string;
    groupName: string;
    orderCount: number;
    orders: DeliveryOrder[];
}

/**
 * DSP 派送运单分组查询响应接口
 * 包含分组后的运单数据和统计信息
 */
export interface DspDeliveryOrdersGroupedResponse {
    totalOrders: number;
    groups: DeliveryOrderGroup[];
}

/**
 * GraphQL 查询数据类型
 * 用于类型安全的 GraphQL 查询结果
 */
export interface GetDspDeliveryOrdersGroupedData {
    getDspDeliveryOrdersGrouped: DspDeliveryOrdersGroupedResponse;
}